import React, { useCallback, useEffect, useState } from "react";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, TriangleAlert, RefreshCw, BookmarkCheck, ArrowUpRight, Clock } from "lucide-react";

const CA = "#C2185B";
const GREEN = "#00863A";
const RED = "#B3261E";
const AMBER = "#B45309";

const when = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) : "—");
const TRIGGER = { sync: "after a wallet sync", prod_pull: "after a production pull", manual: "run by hand", cli: "run by hand", pytest: "from the test suite" };

export default function PreDeployCheck() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const [busy, setBusy] = useState("");
  const [showGrew, setShowGrew] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/regression/last");
      setBaseline(data.baseline || null);
      setHistory(data.reports || []);
      if (!report && (data.reports || []).length) setReport(data.reports[0]);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  }, [report]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/admin/regression/run");
      setReport(data);
      if (data.status === "pass") toast.success(`Pass — ${data.checks} checks, nothing disappeared`);
      else if (data.status === "fail") toast.error(`${data.fails.length} things got worse since the baseline`);
      else toast.info("No baseline saved yet — save one to compare against.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const saveBaseline = async () => {
    setBusy("save");
    try {
      const { data } = await api.post("/admin/regression/baseline");
      toast.success(`Baseline saved — ${data.portfolios} portfolios, ${data.logins} logins`);
      setReport(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const status = report?.status;
  const tone = status === "pass" ? GREEN : status === "fail" ? RED : AMBER;

  return (
    <div className="min-h-screen bg-canvas">
      <Header />
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-7" data-testid="pre-deploy-check-page">
        <div className="flex items-start gap-3 flex-wrap mb-1">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Pre-deploy check</h1>
          <span className="text-[11px] px-2 py-1 rounded-full font-semibold mt-1"
            style={{ background: "rgba(194,24,91,0.10)", color: CA }}>before you ship</span>
        </div>
        <p className="text-ink-2 text-sm md:text-base max-w-3xl mb-6">
          Every tile, every Details row and every wallet count — recorded in CA and BOT rather than
          dollars, so a price move can't cry wolf. Run it before a deploy and it says plainly whether
          anything reads less than it did, or whether anyone can suddenly see more.
        </p>

        <div className="flex items-center gap-2 flex-wrap mb-6">
          <button onClick={run} disabled={!!busy} data-testid="run-check-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-white text-sm font-semibold transition-transform hover:scale-[1.02] disabled:opacity-60"
            style={{ background: CA }}>
            <RefreshCw size={15} className={busy === "run" ? "animate-spin" : ""} />
            {busy === "run" ? "Checking…" : "Run the check"}
          </button>
          {user?.is_super_admin && (
            <button onClick={saveBaseline} disabled={!!busy} data-testid="save-baseline-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border border-line bg-white hover:bg-surface-2 disabled:opacity-60">
              <BookmarkCheck size={15} />
              {busy === "save" ? "Saving…" : "This is correct — make it the baseline"}
            </button>
          )}
          <span className="text-xs text-ink-3 inline-flex items-center gap-1.5" data-testid="baseline-stamp">
            <Clock size={13} /> baseline {when(baseline?.saved_at)}{baseline?.saved_by ? ` · ${baseline.saved_by}` : ""}
          </span>
        </div>

        {report && (
          <div className="rounded-2xl border p-5 mb-5" data-testid="check-result"
            style={{ borderColor: tone, background: status === "pass" ? "rgba(0,134,58,0.05)" : status === "fail" ? "rgba(179,38,30,0.05)" : "rgba(180,83,9,0.05)" }}>
            <div className="flex items-start gap-3">
              {status === "pass" ? <ShieldCheck size={22} style={{ color: tone }} className="mt-0.5" />
                : <TriangleAlert size={22} style={{ color: tone }} className="mt-0.5" />}
              <div className="flex-1">
                <div className="font-semibold text-lg" style={{ color: tone }} data-testid="check-status">
                  {status === "pass" ? "Pass — nothing disappeared"
                    : status === "fail" ? `${report.fails.length} ${report.fails.length === 1 ? "thing" : "things"} got worse`
                    : "No baseline yet"}
                </div>
                <div className="text-xs text-ink-3 mt-0.5">
                  {status === "no_baseline"
                    ? "Save a baseline while the app looks right, then this becomes a real check."
                    : `${report.checks} checks · ${TRIGGER[report.trigger] || report.trigger} · ${when(report.checked_at || report.live_taken_at)}`}
                </div>

                {!!report.fails?.length && (
                  <div className="mt-4 space-y-2" data-testid="check-fails">
                    {report.fails.map((f, i) => (
                      <div key={i} className="rounded-xl bg-white border border-line p-3" data-testid={`fail-row-${i}`}>
                        <div className="text-sm font-semibold">{f.what}</div>
                        <div className="text-xs text-ink-2 mt-0.5">
                          <span className="font-mono">{String(f.was)}</span>
                          <span className="mx-1.5 text-ink-3">→</span>
                          <span className="font-mono font-semibold" style={{ color: RED }}>{String(f.now)}</span>
                          <span className="ml-2 text-ink-3">{f.why}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!!report.changes?.length && (
                  <div className="mt-4">
                    <button onClick={() => setShowGrew((v) => !v)} data-testid="toggle-grew"
                      className="text-xs font-semibold inline-flex items-center gap-1.5" style={{ color: GREEN }}>
                      <ArrowUpRight size={13} />
                      {report.changes.length} {report.changes.length === 1 ? "number" : "numbers"} grew since the baseline
                      <span className="text-ink-3 font-normal">{showGrew ? "hide" : "show"}</span>
                    </button>
                    {showGrew && (
                      <div className="mt-2 grid md:grid-cols-2 gap-1.5" data-testid="grew-list">
                        {report.changes.map((c, i) => (
                          <div key={i} className="text-xs rounded-lg bg-white border border-line px-2.5 py-1.5">
                            <span className="text-ink-2">{c.what}</span>
                            <span className="float-right font-mono">{String(c.was)} → {String(c.now)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-line bg-white p-5">
          <div className="font-semibold mb-1">Checks that ran on their own</div>
          <p className="text-xs text-ink-3 mb-3">
            It also runs itself the moment a wallet sync or a production pull finishes, because that's
            when the numbers actually move.
          </p>
          {history.length === 0 ? (
            <div className="text-sm text-ink-3 py-3" data-testid="history-empty">Nothing yet — the first sync will fill this in.</div>
          ) : (
            <div className="divide-y divide-line" data-testid="check-history">
              {history.map((r, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`history-row-${i}`}>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: r.status === "pass" ? "rgba(0,134,58,0.10)" : "rgba(179,38,30,0.10)", color: r.status === "pass" ? GREEN : RED }}>
                    {r.status === "pass" ? "PASS" : "FAIL"}
                  </span>
                  <span className="text-ink-2">{TRIGGER[r.trigger] || r.trigger}</span>
                  <span className="text-ink-3 text-xs">{when(r.checked_at)}</span>
                  <span className="ml-auto text-xs text-ink-3">
                    {r.fails?.length ? `${r.fails.length} worse` : "nothing worse"}{r.changes?.length ? ` · ${r.changes.length} grew` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <HelpFab />
    </div>
  );
}
